import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useModuleVisibility } from "../hooks/useModuleVisibility";
import { toast } from "sonner";
import api, { downloadBlob } from "../lib/api";
import { PageHeader } from "../components/ui-helpers";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Siren, Clock, FloppyDisk, FilePdf, FileXls, ArrowLeft, PaperPlaneTilt, SealCheck, ArrowUUpLeft, Trash, Warning, ArrowSquareOut } from "@phosphor-icons/react";

const ALERT_STYLE = {
  emergency: "bg-red-100 text-red-800 border-red-300",
  normal: "bg-orange-100 text-orange-800 border-orange-300",
  attempted_cyber: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300",
  system: "bg-sky-100 text-sky-800 border-sky-300",
};
const GROUP_TITLE = {
  emergency: "Emergency Alert — file within 1 hour",
  normal: "Normal Report — file within 6 hours",
  attempted_cyber: "Attempted Cyber Compromise — file by end of next calendar day",
  system: "System Report — file by later of 24 hours or end of next business day",
};

function computeAlert(nums, meta) {
  if (!meta) return { group: null, label: "—", requiresCyber: false };
  const byNum = Object.fromEntries(meta.criteria.map((c) => [c.num, c]));
  const set = new Set((nums || []).map(Number));
  const requiresCyber = [...set].some((n) => byNum[n]?.cyber);
  for (const g of meta.group_priority) {
    if ([...set].some((n) => byNum[n]?.group === g))
      return { group: g, label: meta.alert_groups[g].label, deadline_label: meta.alert_groups[g].deadline_label, requiresCyber };
  }
  return { group: null, label: "—", requiresCyber };
}

function Countdown({ due }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(t); }, []);
  if (!due) return <span className="text-muted-foreground text-xs">Set the incident start time to see the deadline</span>;
  const ms = new Date(due).getTime() - now;
  const past = ms < 0;
  const abs = Math.abs(ms);
  const h = Math.floor(abs / 3600000), m = Math.floor((abs % 3600000) / 60000);
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${past ? "text-red-700" : ms < 3600000 ? "text-orange-700" : "text-green-700"}`}>
      <Clock size={13} weight="fill" /> {past ? "OVERDUE by" : "Due in"} {h}h {m}m · {String(due).replace("T", " ").slice(0, 16)}
    </span>
  );
}

function CheckGroup({ options, value, onChange, disabled, testid }) {
  const set = new Set(value || []);
  const toggle = (opt) => {
    const next = new Set(set);
    next.has(opt) ? next.delete(opt) : next.add(opt);
    onChange([...next]);
  };
  return (
    <div className="grid sm:grid-cols-2 gap-1.5" data-testid={testid}>
      {options.map((opt) => (
        <label key={opt} className={`flex items-start gap-2 text-sm cursor-pointer ${disabled ? "opacity-60" : ""}`}>
          <input type="checkbox" className="mt-0.5" checked={set.has(opt)} disabled={disabled} onChange={() => toggle(opt)} />
          <span>{opt}</span>
        </label>
      ))}
    </div>
  );
}

export default function Eop004Event() {
  const { id } = useParams();
  const nav = useNavigate();
  const { isSubHidden } = useModuleVisibility();
  const [meta, setMeta] = useState(null);
  const [ev, setEv] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState("");
  const [users, setUsers] = useState([]);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [approverId, setApproverId] = useState("");
  const [fileOpen, setFileOpen] = useState(false);
  const [confirmation, setConfirmation] = useState("");

  useEffect(() => {
    api.get("/eop004/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get(`/eop004/events/${id}`).then((r) => setEv(r.data)).catch(() => toast.error("Event not found"));
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
  }, [id]);

  const editable = ev && (ev.status === "draft" || ev.status === "returned");
  const set = useCallback((k, v) => { setEv((p) => ({ ...p, [k]: v })); setDirty(true); }, []);
  const live = useMemo(() => computeAlert(ev?.criteria, meta), [ev?.criteria, meta]);

  const save = async () => {
    setBusy("save");
    try {
      const fields = ["name", "criteria", "foia_line_b", "org_name", "org_address", "geographic_area",
        "incident_began", "incident_ended", "originated", "demand_mw", "demand_unknown", "customers_affected",
        "customers_unknown", "cause", "cause_other", "impact", "impact_other", "action_taken", "action_other",
        "type_comments", "narrative", "cyber_attributes", "restoration_date", "assets_impacted", "foia_narrative",
        "contact_name", "contact_title", "contact_phone", "contact_fax", "contact_email", "notify"];
      const payload = Object.fromEntries(fields.map((f) => [f, ev[f]]));
      const { data } = await api.put(`/eop004/events/${id}`, payload);
      setEv(data); setDirty(false); toast.success("Saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setBusy(""); }
  };

  const doSubmit = async () => {
    if (!approverId) return toast.error("Assign an approver");
    setBusy("submit");
    try {
      if (dirty) await save();
      const { data } = await api.post(`/eop004/events/${id}/submit`, { approver_id: approverId });
      setEv(data); setSubmitOpen(false); toast.success("Submitted for review");
    } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); } finally { setBusy(""); }
  };
  const act = async (verb, body) => {
    setBusy(verb);
    try {
      const { data } = await api.post(`/eop004/events/${id}/${verb}`, body || {});
      setEv(data); toast.success(verb === "approve" ? "Approved" : verb === "return" ? "Returned to draft" : "Updated");
      if (verb === "mark-filed") setFileOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); } finally { setBusy(""); }
  };
  const del = async () => {
    if (!window.confirm("Delete this event report? This cannot be undone.")) return;
    try { await api.delete(`/eop004/events/${id}`); toast.success("Deleted"); nav("/eop004"); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  if (!ev || !meta) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const notify = ev.notify || {};
  const setNotify = (k, v) => set("notify", { ...notify, [k]: v });
  const critByGroup = (g) => meta.criteria.filter((c) => c.group === g);
  const critChecked = new Set((ev.criteria || []).map(Number));
  const toggleCrit = (num) => {
    const s = new Set(critChecked);
    s.has(num) ? s.delete(num) : s.add(num);
    set("criteria", [...s]);
  };

  return (
    <div className="pb-24" data-testid="eop004-event">
      {/* Sticky action bar */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border -mx-6 px-6 py-3 mb-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => nav("/eop004")} data-testid="eop004-back"><ArrowLeft size={16} /></Button>
            <div>
              <div className="flex items-center gap-2">
                <span className={`text-xs border px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${ALERT_STYLE[live.group] || "bg-gray-100 text-gray-600 border-gray-300"}`}>
                  <Siren size={11} weight="fill" /> {live.label}
                </span>
                <span className="text-xs border border-border px-1.5 py-0.5 rounded capitalize" data-testid="eop004-status">{(ev.status || "draft").replace("_", " ")}</span>
                {ev.nerc_eop004_applicable && <span className="text-xs border border-blue-300 bg-blue-50 text-blue-800 px-1.5 py-0.5 rounded">Satisfies NERC EOP-004</span>}
              </div>
              <div className="mt-1"><Countdown due={ev.deadline_primary} /></div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {editable && <Button size="sm" onClick={save} disabled={busy === "save"} data-testid="eop004-save"><FloppyDisk size={15} className="mr-1" />{dirty ? "Save" : "Saved"}</Button>}
            {editable && <Button size="sm" variant="outline" onClick={() => setSubmitOpen(true)} data-testid="eop004-submit"><PaperPlaneTilt size={15} className="mr-1" />Submit for review</Button>}
            {ev.status === "in_review" && <Button size="sm" onClick={() => act("approve")} disabled={busy === "approve"} data-testid="eop004-approve"><SealCheck size={15} className="mr-1" />Approve</Button>}
            {(ev.status === "in_review" || ev.status === "approved") && <Button size="sm" variant="outline" onClick={() => act("return", { note: "" })} data-testid="eop004-return"><ArrowUUpLeft size={15} className="mr-1" />Return to draft</Button>}
            {ev.status === "approved" && <Button size="sm" onClick={() => setFileOpen(true)} data-testid="eop004-file"><ArrowSquareOut size={15} className="mr-1" />File with DOE</Button>}
            {!isSubHidden("eop004:export") && <><Button size="sm" variant="outline" onClick={() => downloadBlob(`/eop004/events/${id}/export.pdf`, "doe417.pdf")} data-testid="eop004-pdf"><FilePdf size={15} className="mr-1" />PDF</Button>
            <Button size="sm" variant="outline" onClick={() => downloadBlob(`/eop004/events/${id}/export.xlsx`, "doe417.xlsx")} data-testid="eop004-xlsx"><FileXls size={15} className="mr-1" />Excel</Button></>}
            {editable && <Button size="sm" variant="ghost" className="text-red-600" onClick={del} data-testid="eop004-delete"><Trash size={15} /></Button>}
          </div>
        </div>
        {ev.status === "returned" && ev.review_note && (
          <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">Returned for changes: {ev.review_note}</div>
        )}
        {ev.status === "filed" && (
          <div className="mt-2 text-xs text-green-800 bg-green-50 border border-green-200 rounded px-2 py-1">
            Filed by {ev.filed_by} on {(ev.filed_at || "").slice(0, 16).replace("T", " ")}{ev.doe_confirmation ? ` · DOE confirmation: ${ev.doe_confirmation}` : ""}
          </div>
        )}
      </div>

      <div className="max-w-4xl">
        <Input value={ev.name} disabled={!editable} onChange={(e) => set("name", e.target.value)}
          className="text-lg font-semibold mb-5" data-testid="eop004-title" placeholder="Event name" />

        {/* Schedule 1 — Criteria */}
        <Section title="Schedule 1 — Alert Criteria" hint="Check every criterion that applies. The alert status and filing deadline are set from the highest-priority group checked.">
          {["emergency", "normal", "attempted_cyber", "system"].map((g) => (
            <div key={g} className="mb-4">
              <div className={`text-xs font-semibold mb-1.5 px-2 py-1 rounded border inline-block ${ALERT_STYLE[g]}`}>{GROUP_TITLE[g]}</div>
              <div className="space-y-1.5">
                {critByGroup(g).map((c) => (
                  <label key={c.num} className={`flex items-start gap-2 text-sm cursor-pointer ${!editable ? "opacity-70" : ""}`} data-testid={`eop004-crit-${c.num}`}>
                    <input type="checkbox" className="mt-1" checked={critChecked.has(c.num)} disabled={!editable} onChange={() => toggleCrit(c.num)} />
                    <span><b>{c.num}.</b> {c.text}{c.cyber ? <em className="text-fuchsia-700"> (requires cyber attributes)</em> : ""}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
          {live.deadline_label && <p className="text-xs text-muted-foreground mt-1">Deadline rule: {live.deadline_label}</p>}
        </Section>

        {/* FOIA line B + Organization */}
        <Section title="Schedule 1 — Organization" >
          {(critChecked.has(2) || critChecked.has(3) || critChecked.has(11) || critChecked.has(14)) && (
            <Field label="B. FOIA Exemption(s) — for boxes 2/3/11/14">
              <CheckGroup options={meta.foia_options} value={ev.foia_line_b} onChange={(v) => set("foia_line_b", v)} disabled={!editable} testid="eop004-foia-b" />
            </Field>
          )}
          <TwoCol>
            <Field label="C. Organization Name"><Input value={ev.org_name} disabled={!editable} onChange={(e) => set("org_name", e.target.value)} /></Field>
            <Field label="D. Address of Principal Business Office"><Input value={ev.org_address} disabled={!editable} onChange={(e) => set("org_address", e.target.value)} /></Field>
          </TwoCol>
        </Section>

        {/* Incident data */}
        <Section title="Schedule 1 — Incident & Disturbance Data">
          <Field label="E. Geographic Area(s) Affected (County, State)"><Input value={ev.geographic_area} disabled={!editable} onChange={(e) => set("geographic_area", e.target.value)} /></Field>
          <TwoCol>
            <Field label="F. Date/Time Incident Began (24-hour)"><Input type="datetime-local" value={ev.incident_began} disabled={!editable} onChange={(e) => set("incident_began", e.target.value)} data-testid="eop004-began" /></Field>
            <Field label="G. Date/Time Incident Ended"><Input type="datetime-local" value={ev.incident_ended} disabled={!editable} onChange={(e) => set("incident_ended", e.target.value)} /></Field>
          </TwoCol>
          <Field label="H. Did the incident originate in your system/area?">
            <div className="flex gap-4">{["Yes", "No", "Unknown"].map((o) => (
              <label key={o} className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                <input type="radio" name="originated" checked={ev.originated === o} disabled={!editable} onChange={() => set("originated", o)} />{o}
              </label>))}</div>
          </Field>
          <TwoCol>
            <Field label="I. Demand Involved (Peak MW)">
              <div className="flex items-center gap-2">
                <Input type="number" value={ev.demand_unknown ? "" : ev.demand_mw} disabled={!editable || ev.demand_unknown} onChange={(e) => set("demand_mw", e.target.value)} className="w-40" />
                <label className="text-xs inline-flex items-center gap-1"><input type="checkbox" checked={!!ev.demand_unknown} disabled={!editable} onChange={(e) => set("demand_unknown", e.target.checked)} />Unknown</label>
              </div>
            </Field>
            <Field label="J. Customers Affected">
              <div className="flex items-center gap-2">
                <Input type="number" value={ev.customers_unknown ? "" : ev.customers_affected} disabled={!editable || ev.customers_unknown} onChange={(e) => set("customers_affected", e.target.value)} className="w-40" />
                <label className="text-xs inline-flex items-center gap-1"><input type="checkbox" checked={!!ev.customers_unknown} disabled={!editable} onChange={(e) => set("customers_unknown", e.target.checked)} />Unknown</label>
              </div>
            </Field>
          </TwoCol>
        </Section>

        {/* Type of emergency */}
        <Section title="Schedule 1 — Type of Emergency">
          <Field label="K. Cause"><CheckGroup options={meta.cause_options} value={ev.cause} onChange={(v) => set("cause", v)} disabled={!editable} testid="eop004-cause" />
            <Input className="mt-2" placeholder="Other cause (describe)" value={ev.cause_other} disabled={!editable} onChange={(e) => set("cause_other", e.target.value)} /></Field>
          <Field label="L. Impact"><CheckGroup options={meta.impact_options} value={ev.impact} onChange={(v) => set("impact", v)} disabled={!editable} />
            <Input className="mt-2" placeholder="Other impact (describe)" value={ev.impact_other} disabled={!editable} onChange={(e) => set("impact_other", e.target.value)} /></Field>
          <Field label="M. Action Taken"><CheckGroup options={meta.action_options} value={ev.action_taken} onChange={(v) => set("action_taken", v)} disabled={!editable} />
            <Input className="mt-2" placeholder="Other action (describe)" value={ev.action_other} disabled={!editable} onChange={(e) => set("action_other", e.target.value)} /></Field>
          <Field label="Additional Information / Comments"><Textarea rows={2} value={ev.type_comments} disabled={!editable} onChange={(e) => set("type_comments", e.target.value)} /></Field>
        </Section>

        {/* Schedule 2 */}
        <Section title="Schedule 2 — Narrative Description">
          <Field label="N. FOIA Exemption(s) for the narrative"><CheckGroup options={meta.foia_options} value={ev.foia_narrative} onChange={(v) => set("foia_narrative", v)} disabled={!editable} /></Field>
          <Field label="T. Narrative (description of the incident and actions taken)">
            <Textarea rows={6} value={ev.narrative} disabled={!editable} onChange={(e) => set("narrative", e.target.value)} data-testid="eop004-narrative"
              placeholder="Cause, change in frequency, mitigation, equipment damaged, critical infrastructure interrupted, effects on other systems, preliminary investigation results, restoration estimate, lost substations/switchyards, islanding boundaries, generators & voltage lines lost." />
          </Field>
          {live.requiresCyber && (
            <Field label="Cyber Attributes (functional impact, attack vector, level of intrusion)">
              <Textarea rows={3} value={ev.cyber_attributes} disabled={!editable} onChange={(e) => set("cyber_attributes", e.target.value)} data-testid="eop004-cyber" />
            </Field>
          )}
          <TwoCol>
            <Field label="U. Estimated Restoration Date"><Input type="date" value={ev.restoration_date} disabled={!editable} onChange={(e) => set("restoration_date", e.target.value)} /></Field>
            <Field label="V. Name of Assets Impacted"><Textarea rows={2} value={ev.assets_impacted} disabled={!editable} onChange={(e) => set("assets_impacted", e.target.value)} /></Field>
          </TwoCol>
          <div className="text-xs font-semibold mt-2 mb-1 text-muted-foreground">Contact for follow-up</div>
          <TwoCol>
            <Field label="O. Name"><Input value={ev.contact_name} disabled={!editable} onChange={(e) => set("contact_name", e.target.value)} /></Field>
            <Field label="P. Title"><Input value={ev.contact_title} disabled={!editable} onChange={(e) => set("contact_title", e.target.value)} /></Field>
            <Field label="Q. Telephone"><Input value={ev.contact_phone} disabled={!editable} onChange={(e) => set("contact_phone", e.target.value)} /></Field>
            <Field label="R. FAX"><Input value={ev.contact_fax} disabled={!editable} onChange={(e) => set("contact_fax", e.target.value)} /></Field>
            <Field label="S. E-mail"><Input value={ev.contact_email} disabled={!editable} onChange={(e) => set("contact_email", e.target.value)} /></Field>
          </TwoCol>
        </Section>

        {/* W. Recipients */}
        <Section title="W. Notify NERC, E-ISAC, or CISA Central" hint="Check the recipients you approve to receive this report. These are notified only when a person files — the app never sends automatically.">
          <div className="space-y-2">
            {meta.recipients.map((r) => (
              <label key={r.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`eop004-notify-${r.key}`}>
                <input type="checkbox" checked={!!notify[r.key]} disabled={!editable} onChange={(e) => setNotify(r.key, e.target.checked)} />
                <span><b>{r.label}</b> <span className="text-muted-foreground font-mono text-xs">{r.email}</span></span>
              </label>
            ))}
          </div>
        </Section>

        {/* Filing hand-off panel */}
        <Section title="File the DOE-417">
          <div className="text-sm bg-amber-50 border border-amber-200 rounded p-3 flex gap-2 items-start">
            <Warning size={18} className="text-amber-700 shrink-0 mt-0.5" />
            <div>This tool never submits to DOE. Download the completed form (PDF/Excel above), then file it yourself at the DOE OE-417 portal, by fax, or by email. Mark it filed here to keep the record.</div>
          </div>
          <div className="grid sm:grid-cols-2 gap-2 mt-3 text-sm">
            <a href={meta.doe_portal_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-blue-700 hover:underline" data-testid="eop004-doe-portal">
              <ArrowSquareOut size={15} /> DOE OE-417 online portal
            </a>
            <span className="text-muted-foreground">FAX: {meta.doe_fax}</span>
            <span className="text-muted-foreground">Email: {meta.doe_email}</span>
            <span className="text-muted-foreground">Phone: {meta.doe_phone}</span>
          </div>
        </Section>
      </div>

      {/* Submit-for-review dialog */}
      <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
        <DialogContent className="max-w-md" data-testid="eop004-submit-dialog">
          <DialogHeader><DialogTitle>Submit for review</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Assign an approver</Label>
            <Select value={approverId} onValueChange={setApproverId}>
              <SelectTrigger data-testid="eop004-approver-select"><SelectValue placeholder="Select approver…" /></SelectTrigger>
              <SelectContent>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email} — {u.role}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">The report locks for editing until the approver approves or returns it.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubmitOpen(false)}>Cancel</Button>
            <Button onClick={doSubmit} disabled={busy === "submit"} data-testid="eop004-submit-confirm">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark-filed dialog */}
      <Dialog open={fileOpen} onOpenChange={setFileOpen}>
        <DialogContent className="max-w-md" data-testid="eop004-file-dialog">
          <DialogHeader><DialogTitle>Mark as filed with DOE</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">Confirm that a person has filed this at DOE. The app does not submit anything.</div>
            <Label>DOE confirmation / reference number (optional)</Label>
            <Input value={confirmation} onChange={(e) => setConfirmation(e.target.value)} data-testid="eop004-confirmation" placeholder="e.g., OE-417 submission ID" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFileOpen(false)}>Cancel</Button>
            <Button onClick={() => act("mark-filed", { doe_confirmation: confirmation, recipients_notified: Object.keys(notify).filter((k) => notify[k]) })} disabled={busy === "mark-filed"} data-testid="eop004-file-confirm">
              Mark filed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Section = ({ title, hint, children }) => (
  <div className="mb-6 border border-border rounded-lg p-4">
    <h3 className="text-base font-semibold text-[#071B3E] mb-1">{title}</h3>
    {hint && <p className="text-xs text-muted-foreground mb-3">{hint}</p>}
    <div className="space-y-3">{children}</div>
  </div>
);
const Field = ({ label, children }) => (
  <div><Label className="mb-1 block text-xs font-medium text-muted-foreground">{label}</Label>{children}</div>
);
const TwoCol = ({ children }) => <div className="grid sm:grid-cols-2 gap-3">{children}</div>;
